import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { ArrowUp, FileSpreadsheet, FileText, Mic, MicOff, Paperclip, Plus, Square, Undo2, X } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { UserChatEditBubble } from './UserChatEditBubble';
import { PdfPreviewModal } from '../../templates/components/PdfPreviewModal';
import { XLSX_MIME } from '../../../utils/spreadsheetPreview';
import { AssistantTimeline } from './AssistantTimeline';
import { ChatMessageToolbar } from './ChatMessageToolbar';
import { withParagraphBreakAfterTool } from '../utils/streamParagraphBreak';
import { classifyAssistantLinkHref } from '../utils/assistantLinkHref';
import { downscaleImageFile } from '../../../utils/downscaleImage';
import { enrichRestoredMessagesWithChatDocuments } from '../utils/enrichRestoredMessages';
import {
  assistantSegmentsFromParts,
  textFromAssistantSegments,
  upsertToolSegment,
  type AssistantSegment,
} from '../utils/assistantSegments';
import {
  ACTIVITY_ERROR_RETRY,
  ACTIVITY_STREAM_ERROR,
  ACTIVITY_THINKING,
  AI_OVERLOADED_CODE,
  AI_OVERLOADED_MESSAGE,
} from '../utils/activityTexts.de';
import { isConfidentEnglish } from '../utils/reasoningLanguageGuard';
import { toolProgressLabel } from '../utils/toolProgressLabels';
import { isModelRefusalFinishReason, MODEL_REFUSAL_MESSAGE } from '../utils/modelRefusal';
import {
  AI_CHAT_CONTINUATION_PROMPT,
  isStepLimitMessage,
  latestPendingRequestId,
  persistedStepCounts,
} from '../utils/chatRunRecovery';
import { BACKGROUND_TURN_DISMISSED_HINT, messageForChatHttpError } from '../utils/chatBusy';
import { ABORTED_TURN_HINT, requestChatCancel, type ChatCancelResult } from '../utils/chatCancel';
import { lastWriteAssistantMessageId } from '../utils/aiWriteTools';
import { sendAiChatFeedback, setActiveAiChatLeaf, undoLastAiWriteTurn, type AiChatFeedbackRating } from '../utils/chatApi';
import {
  isPersistedMessageId,
  leafUnderNode,
  pathFromRoot,
  rememberMessageIdAliases,
  rememberOptimisticTurnAliases,
  resolveActiveLeaf,
  resolvePersistedMessageId,
  siblingMetaById,
} from '../utils/chatTree';

/** Parts compatible with AI SDK UIMessage / convertToModelMessages */
export type ChatUserPart =
  | { type: 'text'; text: string }
  | { type: 'file'; url: string; mediaType: string; filename?: string; documentId?: string };

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  /**
   * Derived full text for copy/search/fallback — concatenated from `text` segments.
   * Not filled directly from the stream anymore.
   */
  text: string;
  /** Nur Assistant: Reasoning, Tool-Fortschritt und Text in Stream-Reihenfolge. */
  assistantSegments?: AssistantSegment[];
  /** Nur Nutzer: für API und Anzeige */
  userParts?: ChatUserPart[];
  /** Persistierte UI-Parts der Assistant-Nachricht (Tool-Calls inkl.). */
  assistantParts?: unknown[];
  /** True when GET synthesized parts from legacy content (heuristic may attach docs). */
  partsLegacy?: boolean;
  /** Skills that were fetched while generating this message */
  skillsLoaded?: string[];
  requestId?: string | null;
  finishReason?: string | null;
  wasAborted?: boolean;
  stepCount?: number;
  maxSteps?: number;
  parentId?: string | null;
  createdAt?: string;
};

type PendingChatAttachment = {
  id: string;
  file: File;
  previewUrl?: string;
};

const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** Keep under Express 100mb JSON limit after base64 (~4/3) expansion. */
const MAX_PENDING_TOTAL_BYTES = 70 * 1024 * 1024;
const MAX_PENDING_ATTACHMENTS = 15;

const ACCEPT_INPUT =
  'image/jpeg,image/png,application/pdf,application/xml,text/xml,.pdf,.xml,.x80,.x81,.x82,.x83,.x85,.x86,.txt,.md,.csv,.xlsx,text/plain,text/csv,text/markdown,' +
  XLSX_MIME;

/**
 * Shift panel-centered blocks to the viewport midpoint (independent of icon-rail width),
 * capped at the slack the panel has left — otherwise the 860px column slides under the
 * sidebars on narrow windows. Belongs on a full-width wrapper: the 50% is the wrapper's
 * own width, 430px is half the widest column inside it. Half the chat-list
 * sidebar comes from `--chat-sidebar-width` (260px offen, 0 zugeklappt).
 */
const VIEWPORT_CENTER =
  'md:-translate-x-[min(calc(var(--chat-sidebar-width,260px)/2)_+_var(--app-nav-width,3.5rem)/2,max(0px,50%_-_430px))]';

/**
 * Nachrichten laufen am oberen Rand aus, statt an einer harten Kante unter dem Titel zu enden.
 * Muss zur oberen Polsterung des Viewports (`pt-10`) passen, damit ganz oben nichts blass wirkt.
 */
const VIEWPORT_TOP_FADE =
  '[-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem)] [mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem)]';

const TEXT_FILE_EXT = /\.(txt|md)$/i;
/** Excel und CSV gehen als Chat-Dokument hoch; der Server liest sie als Text aus. */
const SPREADSHEET_FILE_EXT = /\.(xlsx|csv)$/i;
const GAEB_FILE_EXT = /\.(xml|x80|x81|x82|x83|x85|x86)$/i;

function uniqueStringsInOrder(items: string[] | undefined): string[] {
  if (!items?.length) return [];
  return [...new Set(items)];
}

const AI_WRITE_TOOL_PREFIX = 'request_';
const STREAM_MAX_RETRIES = 3;
const STREAM_RETRY_DELAYS_MS = [2000, 5000, 12000];

class NonRetryableChatError extends Error {
  readonly nonRetryable = true;
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'NonRetryableChatError';
    this.code = code;
  }
}

function isRateLimitErrorText(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('rate limit') ||
    lower.includes('too many requests') ||
    lower.includes('ausgelastet') ||
    lower.includes(AI_OVERLOADED_CODE)
  );
}

async function requestChatTitle(
  chatId: string,
  message: string,
  onTitleGenerated: (chatId: string, title: string) => void,
): Promise<void> {
  try {
    const r = await fetch(`/api/ai/chats/${chatId}/generate-title`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message }),
    });
    if (r.ok) {
      const body = await r.json() as { title?: string };
      onTitleGenerated(chatId, String(body.title ?? 'Neuer Chat'));
    } else {
      const body = await r.text().catch(() => '');
      console.warn('generate-title failed:', r.status, body);
    }
  } catch (err) {
    console.warn('generate-title network error:', err);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Maps browser/network errors (e.g. "Failed to fetch") to user-facing German. */
function formatChatFetchError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '');
  const lower = raw.toLowerCase();
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network request failed') ||
    lower.includes('load failed') ||
    lower.includes('err_internet_disconnected') ||
    lower.includes('err_network_changed')
  ) {
    return 'Die Verbindung zum Server ist unterbrochen. Bitte prüfe dein Netzwerk und versuche es erneut.';
  }
  if (isAbortError(err)) {
    return 'Die Anfrage wurde abgebrochen.';
  }
  const message = raw.trim();
  if (!message) return 'Senden ist fehlgeschlagen.';
  if (isConfidentEnglish(message)) return ACTIVITY_ERROR_RETRY;
  return message;
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function isNonRetryableChatError(err: unknown): boolean {
  return err instanceof NonRetryableChatError || Boolean((err as { nonRetryable?: boolean })?.nonRetryable);
}

function isWriteToolName(toolName: string | undefined): boolean {
  return Boolean(toolName?.startsWith(AI_WRITE_TOOL_PREFIX));
}

/** Human-readable line from a tool execute() result (success/message/error). */
function formatToolOutputText(output: unknown): string | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as Record<string, unknown>;
  const message = typeof o.message === 'string' ? o.message.trim() : '';
  const error = typeof o.error === 'string' ? o.error.trim() : '';
  if (message) return message;
  if (error) return error;
  if (o.success === false) return 'Die Aktion ist fehlgeschlagen.';
  return null;
}

function patchAssistantMessage(
  prev: ChatMessage[],
  assistantId: string,
  updater: (message: ChatMessage) => ChatMessage,
): ChatMessage[] {
  return prev.map((message) => (message.id === assistantId ? updater(message) : message));
}

function withAssistantSegments(message: ChatMessage, segments: AssistantSegment[]): ChatMessage {
  return { ...message, assistantSegments: segments, text: textFromAssistantSegments(segments) };
}

function findLastSegmentIndex(
  segments: AssistantSegment[],
  predicate: (segment: AssistantSegment) => boolean,
): number {
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];
    if (segment && predicate(segment)) return i;
  }
  return -1;
}

type StreamParseMeta = {
  sawTextDelta: boolean;
  sawToolEvent: boolean;
  sawWriteToolOutput: boolean;
  sawStreamError: boolean;
  sawModelRefusal: boolean;
};

function isAllowedBinaryMime(mime: string): boolean {
  const m = mime.split(';')[0].trim().toLowerCase();
  return m === 'image/jpeg' || m === 'image/png' || m === 'application/pdf' || m === 'application/xml' || m === 'text/xml';
}

function isTextLikeFile(file: File): boolean {
  const t = file.type.toLowerCase();
  if (isSpreadsheetFile(file)) return false;
  if (t === 'text/plain' || t === 'text/markdown') return true;
  return TEXT_FILE_EXT.test(file.name);
}

function isSpreadsheetFile(file: File): boolean {
  return SPREADSHEET_FILE_EXT.test(file.name);
}

function isSpreadsheetMediaType(mediaType: string | undefined, filename?: string): boolean {
  return mediaType === XLSX_MIME || mediaType === 'text/csv' || SPREADSHEET_FILE_EXT.test(filename || '');
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    r.readAsDataURL(file);
  });
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Textdatei konnte nicht gelesen werden.'));
    r.readAsText(file, 'UTF-8');
  });
}

/**
 * Data-URL for a chat image part. JPEG/PNG/WebP are already downscaled (or left
 * alone) in addPendingAttachments — do not re-encode them here. GIF still goes
 * through a canvas so the model gets a still PNG (animation is not sent).
 */
async function imageFileToAllowedDataUrl(file: File): Promise<{ dataUrl: string; mediaType: string; filename: string }> {
  const type = file.type.split(';')[0].trim().toLowerCase();
  if (type === 'image/gif') {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Bild konnte nicht verarbeitet werden.');
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
    if (!blob) throw new Error('Bild konnte nicht exportiert werden.');
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Lesen fehlgeschlagen'));
      reader.readAsDataURL(blob);
    });
    const base = file.name.replace(/\.[^.]+$/, '') || 'bild';
    return { dataUrl, mediaType: 'image/png', filename: `${base}.png` };
  }
  const dataUrl = await readFileAsDataURL(file);
  return { dataUrl, mediaType: type || 'image/jpeg', filename: file.name };
}

function normalizeChatDocumentMimeType(mimeType: string | null | undefined, filename?: string): string {
  const mt = String(mimeType || '')
    .toLowerCase()
    .split(';')[0]
    .trim();
  if (mt) return mt;
  const lower = String(filename || '').toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (/\.(jpe?g)$/.test(lower)) return 'image/jpeg';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.xml') || /\.x8[0-6]$/.test(lower)) return 'application/xml';
  if (lower.endsWith('.xlsx')) return XLSX_MIME;
  if (lower.endsWith('.csv')) return 'text/csv';
  return 'application/octet-stream';
}

async function dataUrlToUploadFile(dataUrl: string, filename: string, mediaType: string): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: mediaType });
}

function formatChatDocumentTag(
  documentId: string,
  filename: string,
  mimeType: string,
): string {
  return `[chat-document documentId="${documentId}" filename="${filename}" mimeType="${normalizeChatDocumentMimeType(mimeType, filename)}"]`;
}

async function processFileForChat(file: File): Promise<ChatUserPart | { error: string }> {
  if (file.size > MAX_FILE_BYTES) {
    return { error: `„${file.name}“ ist zu groß (max. ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB).` };
  }
  if (isTextLikeFile(file)) {
    try {
      const body = await readFileAsText(file);
      const snippet = body.length > 24_000 ? `${body.slice(0, 24_000)}\n\n[… gekürzt]` : body;
      return {
        type: 'text',
        text: `Datei „${file.name}“:\n\`\`\`\n${snippet}\n\`\`\`\n`,
      };
    } catch {
      return { error: `„${file.name}“ konnte nicht als Text gelesen werden.` };
    }
  }
  if (isSpreadsheetFile(file)) {
    // Nur hochladen; das Modell bekommt die Textauslese, nicht die Datei.
    const mediaType = /\.xlsx$/i.test(file.name) ? XLSX_MIME : 'text/csv';
    return { type: 'file', url: '', mediaType, filename: file.name };
  }
  const mime = file.type.split(';')[0].trim().toLowerCase();
  if (mime === 'application/pdf') {
    const dataUrl = await readFileAsDataURL(file);
    return { type: 'file', url: dataUrl, mediaType: 'application/pdf', filename: file.name };
  }
  if (mime === 'application/xml' || mime === 'text/xml' || GAEB_FILE_EXT.test(file.name)) {
    const dataUrl = await readFileAsDataURL(file);
    return { type: 'file', url: dataUrl, mediaType: 'application/xml', filename: file.name };
  }
  if (mime === 'image/jpeg' || mime === 'image/png' || mime === 'image/webp' || mime === 'image/gif') {
    try {
      const { dataUrl, mediaType, filename } = await imageFileToAllowedDataUrl(file);
      return { type: 'file', url: dataUrl, mediaType, filename };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Bild konnte nicht verarbeitet werden.' };
    }
  }
  return {
    error: `„${file.name}“: unzulässiger Typ (erlaubt: JPEG, PNG, PDF, XML/GAEB, Excel (.xlsx), CSV, .txt/.md).`,
  };
}

function formatChatDocumentTagFromPart(part: Extract<ChatUserPart, { type: 'file' }>): string {
  return formatChatDocumentTag(
    part.documentId!,
    part.filename || 'dokument',
    part.mediaType,
  );
}

function hasChatDocumentToolTags(parts: ChatUserPart[]): boolean {
  return parts.some((p) => p.type === 'text' && p.text.includes('[chat-document'));
}

/** Ensures hidden [chat-document …] text so tools keep documentId after file parts are stripped. */
function ensureChatDocumentToolTagsInParts(parts: ChatUserPart[]): ChatUserPart[] {
  const fileParts = parts.filter(
    (p): p is Extract<ChatUserPart, { type: 'file' }> => p.type === 'file' && !!p.documentId,
  );
  if (fileParts.length === 0 || hasChatDocumentToolTags(parts)) return parts;
  const tagLines = fileParts.map(formatChatDocumentTagFromPart);
  return [
    ...parts,
    {
      type: 'text' as const,
      text: `\n\nHochgeladene Dateien (für Tool-Aufrufe verwenden):\n${tagLines.join('\n')}`,
    },
  ];
}

/** Removes PDF/image data-URLs from parts; keeps text incl. [chat-document …] tags for tools. */
function stripBinaryFileParts(parts: ChatUserPart[], fallbackText: string): ChatUserPart[] {
  const withTags = ensureChatDocumentToolTagsInParts(parts);
  const filtered = withTags.filter((p) => p.type !== 'file');
  if (filtered.length > 0) return filtered;
  return [{ type: 'text', text: fallbackText.trim() || '(Anhang verarbeitet)' }];
}

function chatMessageToUiPayload(
  m: ChatMessage,
  options?: { stripBinaryFiles?: boolean },
): { id: string; role: 'user' | 'assistant'; parts: unknown[]; skillsLoaded?: string[] } {
  if (m.role === 'assistant') {
    if (m.assistantParts && m.assistantParts.length > 0) {
      return {
        id: m.id,
        role: 'assistant',
        parts: m.assistantParts,
        ...(m.skillsLoaded?.length ? { skillsLoaded: m.skillsLoaded } : {}),
      };
    }
    return {
      id: m.id,
      role: 'assistant',
      parts: [{ type: 'text', text: m.text }],
      ...(m.skillsLoaded?.length ? { skillsLoaded: m.skillsLoaded } : {}),
    };
  }
  if (m.userParts && m.userParts.length > 0) {
    const parts = options?.stripBinaryFiles
      ? stripBinaryFileParts(m.userParts, m.text)
      : m.userParts;
    return { id: m.id, role: 'user', parts };
  }
  return { id: m.id, role: 'user', parts: [{ type: 'text', text: m.text || '' }] };
}

function AssistantMarkdownLink({
  href,
  children,
  className,
  ...rest
}: React.ComponentPropsWithoutRef<'a'>) {
  const linkClass = ['text-blue-600 underline hover:text-blue-800 break-words', className]
    .filter(Boolean)
    .join(' ');
  const target = classifyAssistantLinkHref(href);
  if (target.kind === 'in-app') {
    return (
      <Link to={target.href} {...rest} className={linkClass}>
        {children}
      </Link>
    );
  }
  if (target.kind === 'external') {
    return (
      <a
        href={target.href}
        {...rest}
        className={linkClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  return <span className="break-words">{children}</span>;
}

function hasMarkdownTable(text: string): boolean {
  return /^\s*\|.+\|/m.test(text);
}

const assistantMarkdownComponents: Partial<Components> = {
  a: AssistantMarkdownLink,
  table: ({ children, ...props }) => (
    <div className="my-3 w-full overflow-x-auto">
      <table {...props} className="w-full min-w-full border-collapse text-left text-[13px]">
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th
      {...props}
      className="border border-app-border bg-app-surface-muted px-2.5 py-1.5 font-medium whitespace-nowrap"
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td {...props} className="border border-app-border px-2.5 py-1.5 align-top">
      {children}
    </td>
  ),
};

/**
 * Renders assistant messages as GitHub-Flavored Markdown (lists, tables, links, emphasis).
 * Memoisiert: beim Streamen parst nur der wachsende Abschnitt neu, nicht der ganze Verlauf.
 */
const AssistantMarkdown = React.memo(function AssistantMarkdown({ text }: { text: string }) {
  return (
    <div
      className={[
        'assistant-md max-w-none text-sm text-app-text-muted leading-relaxed',
        '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-0.5',
        // GFM-Checklisten (- [ ] / - [x]): kein Aufzählungspunkt, Checkbox im App-Stil
        '[&_ul.contains-task-list]:list-none [&_ul.contains-task-list]:pl-0.5',
        '[&_li.task-list-item>input]:mr-2 [&_li.task-list-item>input]:h-3.5 [&_li.task-list-item>input]:w-3.5 [&_li.task-list-item>input]:align-[-2px] [&_li.task-list-item>input]:accent-pipedrive-blue',
        '[&_strong]:font-semibold [&_em]:italic',
        '[&_code]:rounded [&_code]:bg-app-surface-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[13px] [&_code]:font-mono',
        '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-app-surface-muted [&_pre]:p-3 [&_pre]:text-[13px]',
        '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-app-border-strong [&_blockquote]:pl-3 [&_blockquote]:text-app-text-muted',
        '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1',
        '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1',
        '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
        '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-0.5',
        '[&_hr]:my-4 [&_hr]:border-app-border',
      ].join(' ')}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={assistantMarkdownComponents}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});

const renderAssistantMarkdown = (text: string) => <AssistantMarkdown text={text} />;

function AssistantChatMessageRow({
  msg,
  isSending,
  onContinue,
  continueDisabled,
  showContinue,
  showUndo,
  undoBusy,
  undoHint,
  onUndo,
  copyText,
  siblingIndex,
  siblingTotal,
  onPrevSibling,
  onNextSibling,
  feedback,
  onFeedback,
}: {
  msg: ChatMessage;
  isSending: boolean;
  onContinue: () => void;
  continueDisabled: boolean;
  showContinue: boolean;
  showUndo: boolean;
  undoBusy: boolean;
  undoHint: string | null;
  onUndo: () => void;
  copyText: string;
  siblingIndex?: number;
  siblingTotal?: number;
  onPrevSibling?: () => void;
  onNextSibling?: () => void;
  feedback?: AiChatFeedbackRating | null;
  onFeedback?: (rating: AiChatFeedbackRating) => void;
}) {
  const wideLayout = Boolean(msg.text && hasMarkdownTable(msg.text));
  const showStepLimit = showContinue && isStepLimitMessage({ role: msg.role, finish_reason: msg.finishReason });
  const showAborted = Boolean(msg.wasAborted) && !isSending;

  return (
    <div className={`group ${wideLayout ? 'w-full min-w-0' : 'max-w-[78%] min-w-0'}`}>
        <AssistantTimeline
          segments={msg.assistantSegments}
          isSending={isSending}
          renderText={renderAssistantMarkdown}
        />
        {showStepLimit || showAborted || showUndo ? (
          <div className="mt-2 flex flex-col gap-1.5">
            {showStepLimit ? (
              <p className="text-xs text-app-text-subtle">
                Das Schrittlimit für diesen Durchgang wurde erreicht.
              </p>
            ) : null}
            {showAborted ? (
              <p className="text-xs text-app-text-subtle">{ABORTED_TURN_HINT}</p>
            ) : null}
            {showStepLimit ? (
              <button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled}
                className="inline-flex h-9 w-fit items-center rounded-md border border-blue-200 bg-app-surface px-3.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Weiterarbeiten
              </button>
            ) : null}
            {showUndo ? (
              <button
                type="button"
                onClick={onUndo}
                disabled={undoBusy}
                title="Letzte Änderung der KI rückgängig machen"
                className="inline-flex items-center gap-1 self-start rounded-md px-0.5 py-0.5 text-xs text-app-text-subtle hover:text-app-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                {undoBusy ? <LoadingSpinner size="xs" /> : <Undo2 className="h-3.5 w-3.5" aria-hidden />}
                Rückgängig
              </button>
            ) : null}
            {showUndo && undoHint ? (
              <p className="text-xs text-app-text-subtle">{undoHint}</p>
            ) : null}
          </div>
        ) : null}
        <ChatMessageToolbar
          align="left"
          copyText={copyText}
          siblingIndex={siblingIndex}
          siblingTotal={siblingTotal}
          onPrevSibling={onPrevSibling}
          onNextSibling={onNextSibling}
          disabled={isSending}
          feedback={feedback}
          onFeedback={onFeedback}
        />
    </div>
  );
}

type ChatMessageItemProps = {
  msg: ChatMessage;
  /** Diese Antwort streamt gerade. */
  isStreaming: boolean;
  isLatestAssistant: boolean;
  isBusy: boolean;
  showUndo: boolean;
  undoBusy: boolean;
  undoHint: string | null;
  feedback: AiChatFeedbackRating | null;
  siblingIndex?: number;
  siblingTotal?: number;
  prevSiblingId?: string;
  nextSiblingId?: string;
  isEditing: boolean;
  /** Nur gesetzt, solange diese Nachricht bearbeitet wird. */
  editDraft: string;
  onContinue: () => void;
  onUndo: () => void;
  onSwitchVersion: (messageId: string) => void;
  onRate: (msg: ChatMessage, rating: AiChatFeedbackRating) => void;
  onStartEdit: (msg: ChatMessage) => void;
  onCancelEdit: () => void;
  onEditDraftChange: (value: string) => void;
  onSubmitEdit: (msg: ChatMessage) => void;
  onPreview: (url: string, filename: string) => void;
};

/**
 * Eine Zeile im Verlauf. Alle Props sind primitiv oder stabil, damit Tippen im Eingabefeld und
 * jedes gestreamte Token nicht jede Nachricht neu rendern (und ihr Markdown neu parsen).
 */
const ChatMessageItem = React.memo(function ChatMessageItem({
  msg,
  isStreaming,
  isLatestAssistant,
  isBusy,
  showUndo,
  undoBusy,
  undoHint,
  feedback,
  siblingIndex,
  siblingTotal,
  prevSiblingId,
  nextSiblingId,
  isEditing,
  editDraft,
  onContinue,
  onUndo,
  onSwitchVersion,
  onRate,
  onStartEdit,
  onCancelEdit,
  onEditDraftChange,
  onSubmitEdit,
  onPreview,
}: ChatMessageItemProps) {
  const goPrev = prevSiblingId ? () => onSwitchVersion(prevSiblingId) : undefined;
  const goNext = nextSiblingId ? () => onSwitchVersion(nextSiblingId) : undefined;
  return (
    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {msg.role === 'assistant' ? (
        <AssistantChatMessageRow
          msg={msg}
          isSending={isStreaming}
          onContinue={onContinue}
          continueDisabled={isBusy}
          showContinue={isLatestAssistant}
          showUndo={showUndo}
          undoBusy={undoBusy}
          undoHint={undoHint}
          onUndo={onUndo}
          copyText={msg.text}
          siblingIndex={siblingIndex}
          siblingTotal={siblingTotal}
          onPrevSibling={goPrev}
          onNextSibling={goNext}
          feedback={feedback}
          onFeedback={canRateMessage(msg) && !isStreaming ? (rating) => onRate(msg, rating) : undefined}
        />
      ) : isEditing ? (
        <UserChatEditBubble
          value={editDraft}
          onChange={onEditDraftChange}
          onCancel={onCancelEdit}
          onSubmit={() => onSubmitEdit(msg)}
          submitDisabled={isBusy || (!editDraft.trim() && !msg.userParts?.some((p) => p.type === 'file'))}
        >
          <UserMessageFileChips parts={msg.userParts} onPreview={onPreview} className="mb-2" />
        </UserChatEditBubble>
      ) : (
        <div className="group max-w-[70%] min-w-0">
          <div className="rounded-2xl rounded-tr-md bg-pipedrive-blue text-white px-3 py-2 text-sm">
          {msg.text.trim() ? <div className="whitespace-pre-wrap">{msg.text}</div> : null}
          <UserMessageFileChips
            parts={msg.userParts}
            onPreview={onPreview}
            className={msg.text.trim() ? 'mt-2' : undefined}
          />
          </div>
          <ChatMessageToolbar
            align="right"
            copyText={userMessageEditableText(msg)}
            canEdit={!isBusy}
            onEdit={() => onStartEdit(msg)}
            siblingIndex={siblingIndex}
            siblingTotal={siblingTotal}
            onPrevSibling={goPrev}
            onNextSibling={goNext}
            disabled={isBusy}
          />
        </div>
      )}
    </div>
  );
});

/** Stabile Funktion, die immer die aktuelle Implementierung aufruft (für memoisierte Kinder). */
function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const ref = React.useRef(fn);
  React.useLayoutEffect(() => {
    ref.current = fn;
  });
  return React.useCallback((...args: A) => ref.current(...args), []);
}

/**
 * Nachladen (Fokus, Hintergrund-Poll) baut alle Nachrichten neu — unveränderte behalten ihr altes
 * Objekt, damit memoisierte Zeilen nicht neu rendern.
 */
function reuseUnchangedMessages(prev: ChatMessage[], next: ChatMessage[]): ChatMessage[] {
  if (prev.length === 0) return next;
  const byId = new Map(prev.map((m) => [m.id, m]));
  return next.map((m) => {
    const old = byId.get(m.id);
    return old && JSON.stringify(old) === JSON.stringify(m) ? old : m;
  });
}

/** Bewertbar sind echte Antworten mit request_id, keine Rückgängig-Notizen. */
function canRateMessage(msg: ChatMessage): boolean {
  return msg.role === 'assistant' && Boolean(msg.requestId) && !String(msg.requestId).startsWith('undo_');
}

function userDisplaySummary(text: string, parts: ChatUserPart[]): string {
  if (text.trim()) return text;
  const names = parts
    .filter((p): p is Extract<ChatUserPart, { type: 'file' }> => p.type === 'file')
    .map((p) => p.filename || 'Anhang');
  if (names.length) return names.join(', ');
  return '(Anhang)';
}

const CHAT_DOCUMENT_TAG_RE =
  /\[chat-document documentId="([^"]+)" filename="([^"]+)" mimeType="([^"]*)"\]/g;

function userMessageEditableText(msg: ChatMessage): string {
  const textPart = msg.userParts?.find((p): p is Extract<ChatUserPart, { type: 'text' }> => p.type === 'text');
  const raw = textPart?.text ?? msg.text;
  const tagRe = new RegExp(CHAT_DOCUMENT_TAG_RE.source, 'gi');
  return raw
    .replace(tagRe, '')
    .replace(/^Hochgeladene Dateien \(für Tool-Aufrufe verwenden\):\s*/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function rebuildUserPartsOnEdit(original: ChatUserPart[] | undefined, newText: string): ChatUserPart[] {
  const files = (original ?? []).filter((p): p is Extract<ChatUserPart, { type: 'file' }> => p.type === 'file');
  const parts: ChatUserPart[] = [];
  if (newText.trim()) parts.push({ type: 'text', text: newText.trim() });
  parts.push(...files);
  return ensureChatDocumentToolTagsInParts(parts);
}

function parseUserMessageContent(content: string): { displayText: string; userParts?: ChatUserPart[] } {
  const docs: Array<{ documentId: string; filename: string; mimeType: string }> = [];
  const tagRe = new RegExp(CHAT_DOCUMENT_TAG_RE.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = tagRe.exec(content)) !== null) {
    docs.push({ documentId: match[1], filename: match[2], mimeType: match[3] });
  }
  const stripped = content.replace(tagRe, '').replace(/\n{3,}/g, '\n\n').trim();
  if (docs.length === 0) return { displayText: content };
  const userParts: ChatUserPart[] = [];
  if (stripped) userParts.push({ type: 'text', text: stripped });
  for (const d of docs) {
    userParts.push({
      type: 'file',
      url: '',
      mediaType: d.mimeType,
      filename: d.filename,
      documentId: d.documentId,
    });
  }
  const displayText = stripped || docs.map((d) => d.filename).join(', ');
  return { displayText, userParts: ensureChatDocumentToolTagsInParts(userParts) };
}

function chatDocumentPreviewUrl(part: Extract<ChatUserPart, { type: 'file' }>): string | null {
  if (part.documentId) return `/api/ai/chat-documents/${part.documentId}`;
  if (part.url) return part.url;
  return null;
}

function UserMessageFileChips({
  parts,
  onPreview,
  className,
}: {
  parts?: ChatUserPart[];
  onPreview: (url: string, filename: string) => void;
  className?: string;
}) {
  if (!parts?.some((p) => p.type === 'file')) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className ?? ''}`}>
      {parts.map((p, i) => {
        if (p.type !== 'file') return null;
        const previewUrl = chatDocumentPreviewUrl(p);
        const openPreview = previewUrl
          ? () => onPreview(previewUrl, p.filename || 'Dokument')
          : undefined;
        if (p.mediaType.startsWith('image/')) {
          return (
            <button
              key={i}
              type="button"
              onClick={openPreview}
              disabled={!openPreview}
              className="rounded-md border border-white/30 disabled:cursor-default"
            >
              <img
                src={p.url || previewUrl || ''}
                alt={p.filename || ''}
                className="max-h-24 max-w-[120px] rounded-md object-cover"
              />
            </button>
          );
        }
        return (
          <button
            key={i}
            type="button"
            onClick={openPreview}
            disabled={!openPreview}
            className="flex items-center gap-1 text-[11px] bg-white/15 rounded px-2 py-1 hover:bg-white/25 transition-colors disabled:cursor-default disabled:hover:bg-white/15"
          >
            {isSpreadsheetMediaType(p.mediaType, p.filename) ? (
              <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <FileText className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="truncate max-w-[140px]">{p.filename || 'PDF'}</span>
          </button>
        );
      })}
    </div>
  );
}

function userPartsFromPersistedParts(parts: unknown[]): ChatUserPart[] | undefined {
  const userParts: ChatUserPart[] = [];
  for (const raw of parts) {
    if (!raw || typeof raw !== 'object') continue;
    const p = raw as { type?: string; text?: string; url?: string; mediaType?: string; filename?: string; documentId?: string };
    if (p.type === 'text' && typeof p.text === 'string') {
      userParts.push({ type: 'text', text: p.text });
    } else if (p.type === 'file') {
      userParts.push({
        type: 'file',
        url: String(p.url || ''),
        mediaType: p.mediaType || 'application/octet-stream',
        filename: p.filename,
        documentId: p.documentId,
      });
    }
  }
  if (userParts.length === 0) return undefined;
  return ensureChatDocumentToolTagsInParts(userParts);
}

function displayTextFromParts(parts: unknown[], fallback: string): string {
  const chunks = parts
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return '';
      const p = raw as { type?: string; text?: string };
      return p.type === 'text' && typeof p.text === 'string' ? p.text : '';
    })
    .filter((t) => t.trim().length > 0);
  if (chunks.length) return chunks.join('');
  return fallback;
}

type PersistedChatDocument = {
  id: string;
  filename: string;
  mime_type: string | null;
  created_at: string;
};

type PersistedChatMessage = {
  id: string;
  role: string;
  content: string;
  skills_loaded?: string[] | null;
  created_at?: string;
  parent_id?: string | null;
  parts?: unknown[] | null;
  parts_legacy?: boolean;
  request_id?: string | null;
  finish_reason?: string | null;
  was_aborted?: boolean;
  usage?: unknown;
};

function treeNodesFromMessages(messages: ChatMessage[]) {
  return messages.map((m) => ({
    id: m.id,
    parentId: m.parentId ?? null,
    createdAt: m.createdAt ?? '',
  }));
}

/** Full transcript for a leaf: shared prefix + chosen version + that version's follow-ups. */
function visibleMessagesFromTree(messages: ChatMessage[], leafId: string | null): ChatMessage[] {
  const byId = new Map(messages.map((m) => [m.id, m]));
  return pathFromRoot(treeNodesFromMessages(messages), leafId)
    .map((node) => byId.get(node.id))
    .filter((m): m is ChatMessage => Boolean(m));
}

const mediaRecorderSupported =
  typeof window !== 'undefined' && Boolean(window.MediaRecorder) && Boolean(navigator.mediaDevices?.getUserMedia);

interface AiChatPanelProps {
  chatId: string | null;
  onChatCreated: (id: string) => void;
  /** Called after the AI has generated and saved a title so the sidebar can refresh. */
  onTitleGenerated: (chatId: string, title: string) => void;
  onChatMetadata?: (chat: {
    id: string;
    title: string;
    updated_at: string;
    pinned_at: string | null;
    access?: 'owner' | 'read';
    visibility?: 'private' | 'members' | 'instance';
    grantee_user_ids?: string[];
  }) => void;
  projectId?: string | null;
}

type SlashSkill = { id: string; name: string; description: string };

export function AiChatPanel({
  chatId,
  onChatCreated,
  onTitleGenerated,
  onChatMetadata,
  projectId = null,
}: AiChatPanelProps) {
  const activeChatIdRef = React.useRef<string | null>(chatId);
  /** Title generation runs after the first successful chat response (not in parallel). */
  const pendingTitleRef = React.useRef<{ chatId: string; message: string } | null>(null);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState('');
  const [slashSkills, setSlashSkills] = React.useState<SlashSkill[]>([]);
  const [slashOpen, setSlashOpen] = React.useState(false);
  const [slashQuery, setSlashQuery] = React.useState('');
  const [slashIndex, setSlashIndex] = React.useState(0);
  const [turnSkills, setTurnSkills] = React.useState<SlashSkill[]>([]);
  const slashLoadedRef = React.useRef(false);
  const [pendingAttachments, setPendingAttachments] = React.useState<PendingChatAttachment[]>([]);
  const [plusOpen, setPlusOpen] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [isRunningInBackground, setIsRunningInBackground] = React.useState(false);
  const [backgroundDismissedHint, setBackgroundDismissedHint] = React.useState<string | null>(null);
  const [streamStepCount, setStreamStepCount] = React.useState(0);
  const [streamMaxSteps, setStreamMaxSteps] = React.useState<number | null>(null);
  const [chatError, setChatError] = React.useState<string | null>(null);
  // true while we're fetching persisted messages for an existing chat —
  // prevents flashing the empty "new chat" screen during the load
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(() => chatId !== null);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [isRecording, setIsRecording] = React.useState(false);
  const [isTranscribing, setIsTranscribing] = React.useState(false);
  const [docPreview, setDocPreview] = React.useState<{ url: string; filename: string } | null>(null);
  const [undoBusy, setUndoBusy] = React.useState(false);
  const [undoHint, setUndoHint] = React.useState<string | null>(null);
  /** Eigene Daumen-Bewertungen je request_id. */
  const [feedbackByRequest, setFeedbackByRequest] = React.useState<Record<string, AiChatFeedbackRating>>({});
  const feedbackByRequestRef = React.useRef<Record<string, AiChatFeedbackRating>>({});
  const [activeLeafId, setActiveLeafId] = React.useState<string | null>(null);
  const [editingUserId, setEditingUserId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState('');
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const audioChunksRef = React.useRef<Blob[]>([]);
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const plusRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const composerTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  /** Synchronous copy of the assistant reply while streaming — used for DB persistence (setState can lag). */
  const assistantStreamBufferRef = React.useRef('');
  /** Tracks write-tool output (retry safety) and paragraph breaks after tool steps. */
  const streamSessionRef = React.useRef({
    sawWriteToolOutput: false,
    needsParagraphBreakBeforeText: false,
  });
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const messagesRef = React.useRef<ChatMessage[]>([]);
  const messageIdAliasRef = React.useRef<Map<string, string>>(new Map());
  const activeLeafIdRef = React.useRef<string | null>(null);
  const isSendingRef = React.useRef(false);
  const activeRequestIdRef = React.useRef<string | null>(null);
  const ignoreBackgroundRunningRef = React.useRef(false);
  /** Laufender POST /api/ai/chat/cancel für den eigenen Stream; der Abort-Pfad wartet darauf. */
  const serverCancelRef = React.useRef<Promise<ChatCancelResult | null> | null>(null);
  const backgroundCancelInFlightRef = React.useRef(false);

  const COMPOSER_MAX_LINES = 5;

  const syncComposerTextareaHeight = React.useCallback(() => {
    const el = composerTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20;
    const maxH = lh * COMPOSER_MAX_LINES + (Number.isFinite(padY) ? padY : 0);
    const next = Math.min(el.scrollHeight, maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    syncComposerTextareaHeight();
  }, [input, syncComposerTextareaHeight]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (plusRef.current && !plusRef.current.contains(e.target as Node)) setPlusOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  /** Nur mitscrollen, solange man unten ist — wer hochscrollt, wird beim Streamen nicht zurückgerissen. */
  const stickToBottomRef = React.useRef(true);
  const renderedMessageCountRef = React.useRef(0);
  const onViewportScroll = React.useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    // Neue Nachricht (gesendet, geladen) → immer nach unten; sonst nur, wenn man schon unten ist.
    const added = messages.length > renderedMessageCountRef.current;
    renderedMessageCountRef.current = messages.length;
    if (added) stickToBottomRef.current = true;
    if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, isSending, isRunningInBackground]);

  React.useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  React.useEffect(() => {
    activeLeafIdRef.current = activeLeafId;
  }, [activeLeafId]);

  React.useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  // Keep activeChatIdRef up-to-date whenever chatId prop changes (e.g. after
  // onChatCreated fires) without triggering a remount or a message reload.
  React.useEffect(() => {
    activeChatIdRef.current = chatId;
    ignoreBackgroundRunningRef.current = false;
    setBackgroundDismissedHint(null);
    setUndoHint(null);
    setUndoBusy(false);
    setEditingUserId(null);
    setActiveLeafId(null);
    messageIdAliasRef.current = new Map();
    feedbackByRequestRef.current = {};
    setFeedbackByRequest({});
  }, [chatId]);

  const loadHistory = React.useCallback(async (options?: {
    silent?: boolean;
    preserveStreaming?: boolean;
  }) => {
    const cid = activeChatIdRef.current;
    if (!cid) return null;
    if (!options?.silent) {
      setIsLoadingHistory(true);
      setHistoryError(null);
    }
    try {
      const res = await fetch(`/api/ai/chats/${cid}/messages`, { credentials: 'include' });
      if (!res.ok) {
        if (!options?.silent) {
          setHistoryError(
            res.status === 429
              ? 'Zu viele Anfragen in kurzer Zeit. Bitte einen Moment warten.'
              : 'Chat konnte nicht geladen werden.',
          );
        }
        return null;
      }
      const data = await res.json() as {
        chat?: { id: string; title: string; updated_at: string; pinned_at?: string | null; active_leaf_id?: string | null };
        messages: PersistedChatMessage[];
        documents?: PersistedChatDocument[];
        isRunning?: boolean;
        feedback?: Record<string, AiChatFeedbackRating>;
      };
      if (data.feedback && typeof data.feedback === 'object') {
        feedbackByRequestRef.current = { ...data.feedback };
        setFeedbackByRequest({ ...data.feedback });
      }
      const rawMessages = data.messages ?? [];
      if (data.chat) {
        onChatMetadata?.({
          ...data.chat,
          pinned_at: data.chat.pinned_at ?? null,
        });
      }
      const restored: ChatMessage[] = rawMessages.map((m) => {
        const partsLegacy = m.parts_legacy !== false;
        const parts = Array.isArray(m.parts) ? m.parts : [];
        const counts = persistedStepCounts(m.usage);
        if (m.role === 'assistant') {
          const assistantSegments = assistantSegmentsFromParts(
            partsLegacy ? null : parts,
            m.content,
          );
          return {
            id: m.id,
            role: 'assistant' as const,
            text: textFromAssistantSegments(assistantSegments) || displayTextFromParts(parts, m.content),
            assistantSegments,
            assistantParts: parts.length > 0 ? parts : undefined,
            skillsLoaded: uniqueStringsInOrder(m.skills_loaded ?? undefined),
            partsLegacy,
            requestId: m.request_id,
            finishReason: m.finish_reason,
            wasAborted: m.was_aborted,
            parentId: m.parent_id ?? null,
            createdAt: m.created_at,
            ...counts,
          };
        }
        const fromParts = parts.length > 0 ? userPartsFromPersistedParts(parts) : undefined;
        const parsed = parseUserMessageContent(m.content);
        return {
          id: m.id,
          role: 'user' as const,
          text: fromParts ? displayTextFromParts(parts, parsed.displayText) : parsed.displayText,
          userParts: fromParts ?? parsed.userParts,
          partsLegacy,
          requestId: m.request_id,
          parentId: m.parent_id ?? null,
          createdAt: m.created_at,
        };
      });
      const enriched = enrichRestoredMessagesWithChatDocuments(
        restored,
        rawMessages,
        data.documents ?? [],
      ) as ChatMessage[];
      const running = data.isRunning === true;
      if (!running) {
        ignoreBackgroundRunningRef.current = false;
        setBackgroundDismissedHint(null);
      }
      const showBackground = running && !ignoreBackgroundRunningRef.current;
      setIsRunningInBackground(showBackground);
      const storedLeaf = typeof data.chat?.active_leaf_id === 'string' ? data.chat.active_leaf_id : null;
      const leaf = resolveActiveLeaf(treeNodesFromMessages(enriched), storedLeaf);
      const pathIds = new Set(pathFromRoot(treeNodesFromMessages(enriched), leaf).map((node) => node.id));
      activeRequestIdRef.current = showBackground
        ? latestPendingRequestId(rawMessages.filter((m) => pathIds.has(m.id)))
        : null;
      if (!(running && options?.preserveStreaming && isSendingRef.current)) {
        rememberMessageIdAliases(messagesRef.current, enriched, messageIdAliasRef.current);
        const shared = reuseUnchangedMessages(messagesRef.current, enriched);
        messagesRef.current = shared;
        setMessages(shared);
        setActiveLeafId(leaf);
        activeLeafIdRef.current = leaf;
      }
      if (!running) {
        setChatError(null);
      }
      return { isRunning: running, messages: enriched, chat: data.chat ?? null };
    } catch {
      if (!options?.silent) {
        setHistoryError('Chat konnte nicht geladen werden.');
      }
      return null;
    } finally {
      if (!options?.silent) {
        setIsLoadingHistory(false);
      }
    }
  }, [onChatMetadata]);

  React.useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    // focus und visibilitychange feuern beim Tab-Wechsel beide — nur einmal nachladen.
    let lastReconcileAt = 0;
    const reconcileVisibleChat = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastReconcileAt < 1_000) return;
      lastReconcileAt = now;
      void loadHistory({ silent: true, preserveStreaming: true });
    };
    document.addEventListener('visibilitychange', reconcileVisibleChat);
    window.addEventListener('focus', reconcileVisibleChat);
    return () => {
      document.removeEventListener('visibilitychange', reconcileVisibleChat);
      window.removeEventListener('focus', reconcileVisibleChat);
    };
  }, [loadHistory]);

  React.useEffect(() => {
    if (!isRunningInBackground) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadHistory({ silent: true, preserveStreaming: true });
      }
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [isRunningInBackground, loadHistory]);

  const addPendingAttachments = React.useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files);
    setChatError(null);
    const processed = await Promise.all(arr.map((file) => downscaleImageFile(file)));
    setPendingAttachments((prev) => {
      let next = [...prev];
      for (const { file } of processed) {
        if (next.length >= MAX_PENDING_ATTACHMENTS) {
          setChatError(`Maximal ${MAX_PENDING_ATTACHMENTS} Dateien gleichzeitig.`);
          break;
        }
        if (file.size > MAX_FILE_BYTES) {
          setChatError(`„${file.name}“ ist zu groß (max. ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB).`);
          continue;
        }
        const pendingTotal = next.reduce((sum, s) => sum + s.file.size, 0);
        if (pendingTotal + file.size > MAX_PENDING_TOTAL_BYTES) {
          setChatError(
            `Anhänge zusammen zu groß (max. ${Math.round(MAX_PENDING_TOTAL_BYTES / (1024 * 1024))} MB insgesamt).`,
          );
          break;
        }
        const mime = file.type.split(';')[0].trim().toLowerCase();
        const isGaebByExt = GAEB_FILE_EXT.test(file.name);
        if (!isTextLikeFile(file) && !isSpreadsheetFile(file) && !isGaebByExt && !isAllowedBinaryMime(mime) && !['image/webp', 'image/gif'].includes(mime)) {
          setChatError(
            /\.xls$/i.test(file.name)
              ? `„${file.name}“: altes Excel-Format — bitte als .xlsx oder CSV speichern.`
              : `„${file.name}“: nur JPEG, PNG, PDF, XML/GAEB, Excel (.xlsx), CSV oder Text (.txt/.md).`,
          );
          continue;
        }
        const id = `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        let previewUrl: string | undefined;
        if (mime.startsWith('image/')) {
          previewUrl = URL.createObjectURL(file);
        }
        next.push({ id, file, previewUrl });
      }
      return next;
    });
  }, []);

  const removePendingAttachment = React.useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const s = prev.find((x) => x.id === id);
      if (s?.previewUrl) URL.revokeObjectURL(s.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  /**
   * Parses one "data:" payload from the Vercel AI SDK v6 UI-message stream.
   *
   * Timeline sources (stream order, never re-sorted):
   *   reasoning-start / reasoning-delta / reasoning-end
   *   data-tool-progress (server, all tools) — preferred for label + summary
   *   tool-input-available — fallback row if progress hasn't arrived yet
   *   text-delta
   *
   * tool-output-available still appends write-tool confirmation as a text
   * segment. The three old pills (fetch_knowledge / search_stammdaten /
   * fetch_webpage) are not rendered anymore.
   */
  const parseSseAndUpdate = React.useCallback((payload: string, assistantId: string): StreamParseMeta => {
    const meta: StreamParseMeta = {
      sawTextDelta: false,
      sawToolEvent: false,
      sawWriteToolOutput: false,
      sawStreamError: false,
      sawModelRefusal: false,
    };
    try {
      const chunk = JSON.parse(payload) as {
        type: string;
        id?: string;
        delta?: string;
        text?: string;
        errorText?: string;
        finishReason?: string;
        rawFinishReason?: string;
        toolName?: string;
        toolCallId?: string;
        input?: unknown;
        output?: unknown;
        data?: unknown;
      };

      const markToolStep = () => {
        meta.sawToolEvent = true;
        streamSessionRef.current.needsParagraphBreakBeforeText = true;
      };

      const appendTextSegment = (addition: string) => {
        if (!addition) return;
        assistantStreamBufferRef.current += addition;
        setMessages((prev) =>
          patchAssistantMessage(prev, assistantId, (message) => {
            const segments = [...(message.assistantSegments ?? [])];
            const last = segments[segments.length - 1];
            if (last?.type === 'text') {
              segments[segments.length - 1] = { type: 'text', text: `${last.text}${addition}` };
            } else {
              segments.push({ type: 'text', text: addition });
            }
            return withAssistantSegments(message, segments);
          }),
        );
      };

      if (chunk.type === 'reasoning-start') {
        const id = typeof chunk.id === 'string' ? chunk.id : undefined;
        setMessages((prev) =>
          patchAssistantMessage(prev, assistantId, (message) => {
            const segments = [...(message.assistantSegments ?? []), { type: 'reasoning' as const, text: '', done: false, id }];
            return withAssistantSegments(message, segments);
          }),
        );
      } else if (chunk.type === 'reasoning-delta') {
        const delta =
          typeof chunk.delta === 'string' && chunk.delta
            ? chunk.delta
            : typeof chunk.text === 'string'
              ? chunk.text
              : '';
        if (delta) {
          setMessages((prev) =>
            patchAssistantMessage(prev, assistantId, (message) => {
              const segments = [...(message.assistantSegments ?? [])];
              const idx = findLastSegmentIndex(
                segments,
                (segment) =>
                  segment.type === 'reasoning' &&
                  (chunk.id ? segment.id === chunk.id : !segment.done),
              );
              if (idx >= 0) {
                const current = segments[idx];
                if (current?.type === 'reasoning') {
                  segments[idx] = { ...current, text: `${current.text}${delta}` };
                }
              } else {
                segments.push({ type: 'reasoning', text: delta, done: false, id: chunk.id });
              }
              return withAssistantSegments(message, segments);
            }),
          );
        }
      } else if (chunk.type === 'reasoning-end') {
        setMessages((prev) =>
          patchAssistantMessage(prev, assistantId, (message) => {
            const segments = [...(message.assistantSegments ?? [])];
            const idx = findLastSegmentIndex(
              segments,
              (segment) =>
                segment.type === 'reasoning' &&
                (chunk.id ? segment.id === chunk.id : !segment.done),
            );
            if (idx >= 0) {
              const current = segments[idx];
              if (current?.type === 'reasoning') segments[idx] = { ...current, done: true };
            }
            return withAssistantSegments(message, segments);
          }),
        );
      } else if (chunk.type === 'data-tool-progress') {
        markToolStep();
        const data = chunk.data && typeof chunk.data === 'object' ? (chunk.data as Record<string, unknown>) : {};
        const tool = typeof data.tool === 'string' ? data.tool.trim() : '';
        const id =
          (typeof chunk.id === 'string' && chunk.id.trim()) ||
          (typeof data.toolCallId === 'string' && data.toolCallId.trim()) ||
          (typeof chunk.toolCallId === 'string' && chunk.toolCallId.trim()) ||
          undefined;
        if (id || tool) {
          const stateRaw = String(data.state ?? 'running');
          const state: 'running' | 'done' | 'error' =
            stateRaw === 'done' || stateRaw === 'error' ? stateRaw : 'running';
          const summary = typeof data.summary === 'string' ? data.summary : undefined;
          const label = tool ? toolProgressLabel(tool, state) : '';
          setMessages((prev) =>
            patchAssistantMessage(prev, assistantId, (message) =>
              withAssistantSegments(
                message,
                upsertToolSegment(message.assistantSegments ?? [], {
                  type: 'tool',
                  tool,
                  label,
                  state,
                  ...(summary ? { summary } : {}),
                  id,
                }),
              ),
            ),
          );
        }
      } else if (chunk.type === 'text-delta') {
        const textDelta =
          typeof chunk.delta === 'string' && chunk.delta
            ? chunk.delta
            : typeof chunk.text === 'string'
              ? chunk.text
              : '';
        if (textDelta) {
          meta.sawTextDelta = true;
          const needsBreak = streamSessionRef.current.needsParagraphBreakBeforeText;
          streamSessionRef.current.needsParagraphBreakBeforeText = false;
          const delta = withParagraphBreakAfterTool(
            assistantStreamBufferRef.current,
            textDelta,
            needsBreak,
          );
          appendTextSegment(delta);
        }
      } else if (chunk.type === 'tool-output-available') {
        markToolStep();
        if (typeof chunk.toolCallId === 'string' && chunk.toolCallId) {
          const toolName = typeof chunk.toolName === 'string' ? chunk.toolName.trim() : '';
          setMessages((prev) =>
            patchAssistantMessage(prev, assistantId, (message) =>
              withAssistantSegments(
                message,
                upsertToolSegment(message.assistantSegments ?? [], {
                  type: 'tool',
                  tool: toolName,
                  label: toolName ? toolProgressLabel(toolName, 'done') : '',
                  state: 'done',
                  id: chunk.toolCallId,
                }),
              ),
            ),
          );
        }
        if (isWriteToolName(chunk.toolName)) {
          meta.sawWriteToolOutput = true;
          streamSessionRef.current.sawWriteToolOutput = true;
          const line = formatToolOutputText(chunk.output);
          if (line) {
            const prefix = assistantStreamBufferRef.current.trim() ? '\n\n' : '';
            appendTextSegment(`${prefix}${line}`);
          }
        }
      } else if (chunk.type === 'tool-input-available') {
        markToolStep();
        const toolName = chunk.toolName || '';
        const id = typeof chunk.toolCallId === 'string' ? chunk.toolCallId : undefined;
        if (toolName) {
          setMessages((prev) =>
            patchAssistantMessage(prev, assistantId, (message) =>
              withAssistantSegments(
                message,
                upsertToolSegment(message.assistantSegments ?? [], {
                  type: 'tool',
                  tool: toolName,
                  label: toolProgressLabel(toolName, 'running'),
                  state: 'running',
                  id,
                }),
              ),
            ),
          );
        }
      } else if (typeof chunk.type === 'string' && chunk.type.startsWith('tool-')) {
        markToolStep();
      } else if (chunk.type === 'finish-step') {
        setStreamStepCount((count) => count + 1);
      } else if (chunk.type === 'finish') {
        const refused = isModelRefusalFinishReason(chunk.finishReason)
          || isModelRefusalFinishReason(chunk.rawFinishReason);
        if (refused) {
          meta.sawModelRefusal = true;
          if (!assistantStreamBufferRef.current.trim()) {
            assistantStreamBufferRef.current = MODEL_REFUSAL_MESSAGE;
            setMessages((prev) =>
              patchAssistantMessage(prev, assistantId, (message) =>
                withAssistantSegments(message, [{ type: 'text', text: MODEL_REFUSAL_MESSAGE }]),
              ),
            );
          }
        }
      } else if (chunk.type === 'error') {
        meta.sawStreamError = true;
        const rawError = chunk.errorText?.trim() || ACTIVITY_STREAM_ERROR;
        const overloaded = rawError === AI_OVERLOADED_CODE || rawError.includes(AI_OVERLOADED_CODE);
        const msg = overloaded
          ? AI_OVERLOADED_MESSAGE
          : isConfidentEnglish(rawError) ? ACTIVITY_ERROR_RETRY : rawError;
        assistantStreamBufferRef.current = msg;
        setMessages((prev) =>
          patchAssistantMessage(prev, assistantId, (message) =>
            withAssistantSegments(message, [{ type: 'text', text: msg }]),
          ),
        );
        setChatError(msg);
      }
    } catch {
      // ignore malformed chunks
    }
    return meta;
  }, []);

  const sendMessage = React.useCallback(async (
    messageOverride?: string,
    options?: {
      parentMessageId?: string | null;
      prefixMessages?: ChatMessage[];
      existingUserParts?: ChatUserPart[];
    },
  ) => {
    const text = (messageOverride ?? input).trim();
    const reuseParts = options?.existingUserParts
      ? rebuildUserPartsOnEdit(options.existingUserParts, text)
      : null;
    const attachmentsForTurn = messageOverride === undefined && !reuseParts ? pendingAttachments : [];
    if ((!text && attachmentsForTurn.length === 0 && !reuseParts) || isSending || isRunningInBackground) return;

    const userId = `u_${Date.now()}`;
    const assistantId = `a_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const requestId = crypto.randomUUID();
    const rawParentId = options && Object.prototype.hasOwnProperty.call(options, 'parentMessageId')
      ? (options.parentMessageId ?? null)
      : activeLeafIdRef.current;
    const persistedParentId = resolvePersistedMessageId(rawParentId, messageIdAliasRef.current);
    const parentId = persistedParentId ?? rawParentId;
    const prefixSource = options?.prefixMessages
      ?? visibleMessagesFromTree(messagesRef.current, activeLeafIdRef.current);

    const builtParts: ChatUserPart[] = reuseParts ? [...reuseParts] : [];
    const pendingFileParts: ChatUserPart[] = [];
    if (!reuseParts && text) builtParts.push({ type: 'text', text });

    // Ensure a chat row exists before staging files so uploads get a chat_id.
    if (!activeChatIdRef.current) {
      try {
        const createRes = await fetch('/api/ai/chats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: 'Neuer Chat',
            ...(projectId ? { projectId } : {}),
          }),
        });
        if (createRes.ok) {
          const newChat = await createRes.json() as { id: string };
          activeChatIdRef.current = newChat.id;
          onChatCreated(newChat.id);
        }
      } catch {
        // silently ignore — chat will still work, just won't be persisted
      }
    }

    // Persist binary files server-side so AI tools can reference them by documentId.
    const chatDocumentMeta: string[] = [];
    for (const s of attachmentsForTurn) {
      const r = await processFileForChat(s.file);
      if ('error' in r) {
        setChatError(r.error);
        return;
      }
      if (r.type === 'text') {
        builtParts.push(r);
        continue;
      }

      let uploadFile = s.file;
      if (r.url.startsWith('data:') && r.mediaType.startsWith('image/')) {
        try {
          uploadFile = await dataUrlToUploadFile(
            r.url,
            r.filename || s.file.name,
            r.mediaType,
          );
        } catch {
          setChatError(`„${s.file.name}“ konnte nicht für den Upload vorbereitet werden.`);
          return;
        }
      }

      try {
        const fd = new FormData();
        fd.append('file', uploadFile, uploadFile.name);
        if (activeChatIdRef.current) {
          fd.append('chatId', activeChatIdRef.current);
        }
        const uploadRes = await fetch('/api/ai/chat-documents', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!uploadRes.ok) {
          let errMsg = 'Datei konnte nicht gespeichert werden.';
          try {
            const err = await uploadRes.json() as { error?: string };
            if (err.error) errMsg = err.error;
          } catch {
            /* keep default */
          }
          setChatError(`„${s.file.name}“: ${errMsg}`);
          return;
        }
        const meta = await uploadRes.json() as { documentId: string; filename: string; mimeType: string };
        const docId = meta.documentId;
        const tagFilename = r.filename || meta.filename || uploadFile.name;
        const tagMime = normalizeChatDocumentMimeType(r.mediaType || meta.mimeType, tagFilename);
        chatDocumentMeta.push(formatChatDocumentTag(docId, tagFilename, tagMime));
        pendingFileParts.push({
          type: 'file',
          url: '',
          mediaType: tagMime,
          filename: tagFilename,
          documentId: docId,
        });
      } catch {
        setChatError(`„${s.file.name}“ konnte nicht gespeichert werden.`);
        return;
      }
    }

    // Document IDs must appear in text before image file parts so tools can resolve them.
    if (chatDocumentMeta.length > 0) {
      const tagBlock = `Hochgeladene Dateien (für Tool-Aufrufe verwenden):\n${chatDocumentMeta.join('\n')}`;
      if (builtParts.length > 0 && builtParts[0].type === 'text') {
        builtParts[0] = { type: 'text', text: `${builtParts[0].text}\n\n${tagBlock}` };
      } else {
        builtParts.unshift({ type: 'text', text: tagBlock });
      }
    }
    builtParts.push(...pendingFileParts);

    const displayText = userDisplaySummary(text, builtParts);

    if (activeChatIdRef.current && !pendingTitleRef.current) {
      pendingTitleRef.current = { chatId: activeChatIdRef.current, message: displayText };
    }

    attachmentsForTurn.forEach((s) => {
      if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    });
    const skillIdsForTurn = messageOverride === undefined ? turnSkills.map((skill) => skill.id) : [];
    if (messageOverride === undefined) {
      setInput('');
      setPendingAttachments([]);
      setTurnSkills([]);
      setSlashOpen(false);
    }
    setPlusOpen(false);
    setChatError(null);
    setEditingUserId(null);

    assistantStreamBufferRef.current = '';
    streamSessionRef.current = { sawWriteToolOutput: false, needsParagraphBreakBeforeText: false };
    const createdAt = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      { id: userId, role: 'user', text: displayText, userParts: builtParts, parentId, createdAt, requestId },
      { id: assistantId, role: 'assistant', text: '', assistantSegments: [], parentId: userId, createdAt, requestId },
    ]);
    setActiveLeafId(assistantId);
    activeLeafIdRef.current = assistantId;
    setIsSending(true);
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    let turnRequestId: string | null = null;
    let preloadedSkillsForTurn: string[] = [];

    try {
      const historyPayload = prefixSource
        .filter(
          (m) =>
            m.role === 'assistant'
              ? m.text.trim().length > 0
                || (m.assistantSegments?.length ?? 0) > 0
                || (m.assistantParts?.length ?? 0) > 0
              : m.text.trim().length > 0 || (m.userParts && m.userParts.length > 0),
        )
        .map((m) => chatMessageToUiPayload(m, { stripBinaryFiles: true }));

      const newUserPayload = { id: userId, role: 'user' as const, parts: builtParts };
      turnRequestId = requestId;
      activeRequestIdRef.current = requestId;
      setStreamStepCount(0);
      setStreamMaxSteps(null);
      const parentPayload = persistedParentId !== null || rawParentId == null
        ? { parentMessageId: persistedParentId }
        : {};
      const chatPayload = {
        messages: [...historyPayload, newUserPayload],
        requestId,
        ...parentPayload,
        ...(activeChatIdRef.current ? { chatId: activeChatIdRef.current } : {}),
        ...(skillIdsForTurn.length > 0 ? { skillIds: skillIdsForTurn } : {}),
      };

      const consumeUiMessageStream = async (body: ReadableStream<Uint8Array>) => {
        const reader = body.getReader();
        const signal = abortController.signal;
        const decoder = new TextDecoder();
        let sseBuffer = '';
        let sawTextDelta = false;
        let sawToolEvent = false;
        let sawWriteToolOutput = false;
        let sawStreamError = false;
        let sawModelRefusal = false;
        while (true) {
          if (signal.aborted) {
            await reader.cancel().catch(() => undefined);
            throw new DOMException('Aborted', 'AbortError');
          }
          const { value, done } = await reader.read();
          if (done) break;
          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() || '';
          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line.startsWith('data:')) continue;
            const payloadText = line.slice(5).trim();
            if (!payloadText || payloadText === '[DONE]') continue;
            const meta = parseSseAndUpdate(payloadText, assistantId);
            sawTextDelta = sawTextDelta || meta.sawTextDelta;
            sawToolEvent = sawToolEvent || meta.sawToolEvent;
            sawWriteToolOutput = sawWriteToolOutput || meta.sawWriteToolOutput;
            sawStreamError = sawStreamError || meta.sawStreamError;
            sawModelRefusal = sawModelRefusal || meta.sawModelRefusal;
          }
        }
        return { sawTextDelta, sawToolEvent, sawWriteToolOutput, sawStreamError, sawModelRefusal };
      };

      let streamMeta = {
        sawTextDelta: false,
        sawToolEvent: false,
        sawWriteToolOutput: false,
        sawStreamError: false,
        sawModelRefusal: false,
      };
      let lastStreamError: Error | null = null;

      for (let attempt = 0; attempt < STREAM_MAX_RETRIES; attempt++) {
        if (abortController.signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        if (attempt > 0) {
          const prevErr = assistantStreamBufferRef.current;
          const delayMs = isRateLimitErrorText(prevErr)
            ? Math.max(STREAM_RETRY_DELAYS_MS[attempt - 1] ?? 12000, 15000)
            : (STREAM_RETRY_DELAYS_MS[attempt - 1] ?? 12000);
          await sleep(delayMs);
          if (abortController.signal.aborted) {
            throw new DOMException('Aborted', 'AbortError');
          }
          assistantStreamBufferRef.current = '';
          streamSessionRef.current = { sawWriteToolOutput: false, needsParagraphBreakBeforeText: false };
          setStreamStepCount(0);
          setChatError(null);
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, text: '', assistantSegments: [] } : m)),
          );
        }

        try {
          const res = await fetch('/api/ai/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            signal: abortController.signal,
            body: JSON.stringify(chatPayload),
          });
          if (!res.ok || !res.body) {
            const errBody = await res.text().catch(() => '');
            const parsed = messageForChatHttpError(res.status, errBody);
            if (parsed.busy) {
              throw new NonRetryableChatError(parsed.message, 'ai_chat_busy');
            }
            throw new Error(parsed.message);
          }
          const maxStepsHeader = Number(res.headers.get('x-heizungpro-ai-max-steps'));
          if (Number.isFinite(maxStepsHeader) && maxStepsHeader > 0) {
            setStreamMaxSteps(maxStepsHeader);
          }
          preloadedSkillsForTurn = uniqueStringsInOrder(
            (res.headers.get('x-ai-preloaded-skills') ?? "")
              .split(",")
              .map((slug) => slug.trim())
              .filter(Boolean),
          );
          streamMeta = await consumeUiMessageStream(res.body);
          lastStreamError = null;
          // Provider overload mid-stream: retry like a network error unless a write tool ran.
          if (
            streamMeta.sawStreamError &&
            isRateLimitErrorText(assistantStreamBufferRef.current) &&
            !streamSessionRef.current.sawWriteToolOutput &&
            attempt < STREAM_MAX_RETRIES - 1
          ) {
            continue;
          }
          break;
        } catch (streamErr) {
          if (isAbortError(streamErr)) {
            throw streamErr;
          }
          if (isNonRetryableChatError(streamErr)) {
            throw streamErr;
          }
          lastStreamError =
            streamErr instanceof Error
              ? new Error(formatChatFetchError(streamErr))
              : new Error(formatChatFetchError(streamErr));
          // Only skip retry after a write tool ran — partial read-only text may retry safely.
          if (streamSessionRef.current.sawWriteToolOutput) {
            break;
          }
          if (attempt === STREAM_MAX_RETRIES - 1) {
            throw lastStreamError;
          }
        }
      }

      if (lastStreamError && !assistantStreamBufferRef.current.trim()) {
        throw lastStreamError;
      }
      if (streamMeta.sawStreamError) {
        throw new Error(
          assistantStreamBufferRef.current.trim() ||
            'Die Anfrage wurde vom Modell-Anbieter abgelehnt. Bitte versuche es erneut oder stelle die Frage kürzer.',
        );
      }

      const rawBuf = assistantStreamBufferRef.current;
      const fallbackNoText = streamMeta.sawWriteToolOutput
        ? 'Die Aktion wurde ausgeführt, aber es gab keine Bestätigung vom Assistenten.'
        : streamMeta.sawToolEvent
          ? 'Die KI hat die Daten geladen, konnte aber keine Antwort mehr schreiben. Bitte stelle die Frage kürzer oder öffne einen neuen Chat.'
          : streamMeta.sawModelRefusal
            ? MODEL_REFUSAL_MESSAGE
          : streamMeta.sawStreamError
            ? 'Die Anfrage wurde vom Modell-Anbieter abgelehnt. Bitte versuche es erneut oder stelle die Frage kürzer.'
            : 'Die KI konnte die Anfrage nicht verarbeiten. Bei komplexen Dokumenten kann das vorkommen — bitte versuche es erneut oder stelle eine spezifischere Frage.';
      const resolvedAssistantText = rawBuf.trim() ? rawBuf : fallbackNoText;

      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantId) return m;
          const fromState = m.text.trim();
          const text = fromState || resolvedAssistantText;
          const segments =
            (m.assistantSegments?.length ?? 0) > 0
              ? m.assistantSegments
              : [{ type: 'text' as const, text }];
          return {
            ...withAssistantSegments(m, segments ?? [{ type: 'text', text }]),
            ...(preloadedSkillsForTurn.length
              ? {
                  skillsLoaded: uniqueStringsInOrder([
                    ...(m.skillsLoaded ?? []),
                    ...preloadedSkillsForTurn,
                  ]),
                }
              : {}),
          };
        }),
      );

      const resolvedChatId = activeChatIdRef.current;
      if (resolvedChatId) {
        await loadHistory({ silent: true, preserveStreaming: true });
        rememberOptimisticTurnAliases(
          messageIdAliasRef.current,
          { userId, assistantId },
          visibleMessagesFromTree(messagesRef.current, activeLeafIdRef.current),
        );
        const pendingTitle = pendingTitleRef.current;
        if (
          pendingTitle?.chatId === resolvedChatId &&
          !streamMeta.sawStreamError &&
          (streamMeta.sawTextDelta || streamMeta.sawToolEvent)
        ) {
          pendingTitleRef.current = null;
          void requestChatTitle(resolvedChatId, pendingTitle.message, onTitleGenerated);
        }
      }
    } catch (e) {
      if (isAbortError(e)) {
        setChatError(null);
        // Stop: erst den Server-Abbruch abwarten (Zwischenstand gespeichert, Sperre
        // frei), dann den gespeicherten Stand laden. Ohne Server-Abbruch (Turn läuft
        // nicht in diesem Prozess) bleibt es beim bisherigen „Anzeige beenden“.
        const serverCancel = serverCancelRef.current ? await serverCancelRef.current : null;
        serverCancelRef.current = null;
        if (!serverCancel?.cancelled) dismissLiveDisplay();
        const recovered = await loadHistory({ silent: true, preserveStreaming: true });
        if (!recovered?.isRunning) {
          const partial = assistantStreamBufferRef.current.trim();
          if (partial) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? withAssistantSegments(m, m.assistantSegments?.length ? m.assistantSegments : [{ type: 'text', text: partial }])
                  : m,
              ),
            );
          } else {
            setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          }
        }
      } else if (e instanceof NonRetryableChatError && e.code === 'ai_chat_busy') {
        await loadHistory({ silent: true });
        setBackgroundDismissedHint(null);
        setChatError(e.message);
      } else {
        const recovered = await loadHistory({ silent: true });
        if (recovered?.isRunning) {
          setChatError(null);
        } else {
          const requestWasPersisted = recovered?.messages.some(
            (message) =>
              message.role === 'assistant'
              && message.requestId === turnRequestId,
          );
          if (!requestWasPersisted) {
            const msg = formatChatFetchError(e);
            assistantStreamBufferRef.current = msg;
            setChatError(msg);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? withAssistantSegments(m, [{ type: 'text', text: msg }]) : m,
              ),
            );
            pendingTitleRef.current = null;
          }
        }
      }
    } finally {
      abortControllerRef.current = null;
      setIsSending(false);
    }
  }, [
    input,
    isSending,
    isRunningInBackground,
    pendingAttachments,
    parseSseAndUpdate,
    loadHistory,
    onChatCreated,
    onTitleGenerated,
    projectId,
    turnSkills,
  ]);

  const dismissLiveDisplay = React.useCallback(() => {
    ignoreBackgroundRunningRef.current = true;
    setIsRunningInBackground(false);
    activeRequestIdRef.current = null;
    setBackgroundDismissedHint(BACKGROUND_TURN_DISMISSED_HINT);
  }, []);

  const cancelMessage = React.useCallback(async () => {
    const chatId = activeChatIdRef.current;
    const requestId = activeRequestIdRef.current;
    const controller = abortControllerRef.current;
    if (controller) {
      // Eigener Stream: Server-Abbruch anstoßen, dann den Fetch beenden. Den Rest
      // erledigt der Abort-Pfad in sendMessage, sobald der Server geantwortet hat.
      serverCancelRef.current = requestChatCancel({ chatId, requestId }).catch(() => null);
      controller.abort();
      return;
    }
    // Turn aus einem anderen Tab oder nach Reload: abbrechen, sonst nur Anzeige beenden.
    if (backgroundCancelInFlightRef.current) return;
    backgroundCancelInFlightRef.current = true;
    try {
      const result = await requestChatCancel({ chatId, requestId }).catch(() => null);
      if (result?.cancelled) {
        await loadHistory({ silent: true });
      } else {
        dismissLiveDisplay();
      }
    } finally {
      backgroundCancelInFlightRef.current = false;
    }
  }, [dismissLiveDisplay, loadHistory]);

  const continueAfterStepLimit = React.useCallback(async () => {
    const restored = await loadHistory({ silent: true });
    if (!restored || restored.isRunning) return;
    await sendMessage(AI_CHAT_CONTINUATION_PROMPT);
  }, [loadHistory, sendMessage]);

  const rateAssistantMessage = React.useCallback(async (msg: ChatMessage, rating: AiChatFeedbackRating) => {
    const cid = activeChatIdRef.current;
    const requestId = msg.requestId;
    if (!cid || !requestId) return;
    const previous = feedbackByRequestRef.current[requestId] ?? null;
    const next = previous === rating ? null : rating;
    const apply = (value: AiChatFeedbackRating | null) => {
      const updated = { ...feedbackByRequestRef.current };
      if (value) updated[requestId] = value;
      else delete updated[requestId];
      feedbackByRequestRef.current = updated;
      setFeedbackByRequest(updated);
    };
    apply(next);
    try {
      await sendAiChatFeedback(cid, msg.id, requestId, next);
    } catch {
      apply(previous);
    }
  }, []);

  const undoLastWrite = React.useCallback(async () => {
    const cid = activeChatIdRef.current;
    if (!cid || undoBusy) return;
    setUndoBusy(true);
    setUndoHint(null);
    try {
      const result = await undoLastAiWriteTurn(cid);
      setMessages((prev) => {
        let next = prev.map((message) => {
          if (!result.updatedMessage || message.id !== result.updatedMessage.id) return message;
          return { ...message, assistantParts: result.updatedMessage.parts };
        });
        if (result.noteRow) {
          const segments = assistantSegmentsFromParts(result.noteRow.parts, result.noteRow.content);
          next = [
            ...next,
            {
              id: result.noteRow.id,
              role: "assistant" as const,
              text: result.noteRow.content,
              assistantSegments: segments,
              assistantParts: result.noteRow.parts,
              requestId: result.noteRow.request_id,
              parentId: activeLeafIdRef.current,
              createdAt: new Date().toISOString(),
            },
          ];
        }
        return next;
      });
      if (result.noteRow) {
        setActiveLeafId(result.noteRow.id);
        activeLeafIdRef.current = result.noteRow.id;
      }
      setUndoHint(result.noteRow ? null : result.alreadyUndone ? "Diese Änderungen waren bereits rückgängig." : result.note);
    } catch (error) {
      setUndoHint(error instanceof Error ? error.message : "Rückgängig machen ist fehlgeschlagen.");
    } finally {
      setUndoBusy(false);
    }
  }, [undoBusy]);

  const switchToMessageVersion = React.useCallback(async (messageId: string) => {
    if (isSending || isRunningInBackground) return;
    // Leaf under the chosen version; the viewport is still pathFromRoot(leaf) (prefix + suffix).
    const leaf = leafUnderNode(treeNodesFromMessages(messagesRef.current), messageId, activeLeafIdRef.current);
    if (!leaf) return;
    setActiveLeafId(leaf);
    activeLeafIdRef.current = leaf;
    setEditingUserId(null);
    const cid = activeChatIdRef.current;
    if (cid && isPersistedMessageId(messageId)) {
      try {
        const serverLeaf = await setActiveAiChatLeaf(cid, messageId);
        setActiveLeafId(serverLeaf);
        activeLeafIdRef.current = serverLeaf;
      } catch {
        /* keep optimistic leaf */
      }
    }
  }, [isSending, isRunningInBackground]);

  const submitEditedUserMessage = React.useCallback((msg: ChatMessage) => {
    const nextText = editDraft.trim();
    if (!nextText && !(msg.userParts?.some((p) => p.type === 'file'))) return;
    const ancestors = visibleMessagesFromTree(messagesRef.current, msg.id).slice(0, -1);
    const rawParent = msg.parentId ?? ancestors[ancestors.length - 1]?.id ?? null;
    void sendMessage(nextText, {
      parentMessageId: resolvePersistedMessageId(rawParent, messageIdAliasRef.current) ?? rawParent,
      prefixMessages: ancestors,
      existingUserParts: msg.userParts,
    });
  }, [editDraft, sendMessage]);

  const onContinueItem = useStableCallback(() => void continueAfterStepLimit());
  const onUndoItem = useStableCallback(() => void undoLastWrite());
  const onSwitchVersionItem = useStableCallback((messageId: string) => void switchToMessageVersion(messageId));
  const onRateItem = useStableCallback((msg: ChatMessage, rating: AiChatFeedbackRating) =>
    void rateAssistantMessage(msg, rating));
  const onStartEditItem = useStableCallback((msg: ChatMessage) => {
    setEditingUserId(msg.id);
    setEditDraft(userMessageEditableText(msg));
  });
  const onCancelEditItem = useStableCallback(() => setEditingUserId(null));
  const onSubmitEditItem = useStableCallback((msg: ChatMessage) => submitEditedUserMessage(msg));
  const onPreviewItem = useStableCallback((url: string, filename: string) => setDocPreview({ url, filename }));

  const onPaste = React.useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items?.length) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        addPendingAttachments(files);
      }
    },
    [addPendingAttachments],
  );

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files?.length) addPendingAttachments(e.dataTransfer.files);
    },
    [addPendingAttachments],
  );

  const startRecording = React.useCallback(async () => {
    if (!mediaRecorderSupported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        if (blob.size < 1000) return; // too short, skip
        setIsTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, `audio.${mimeType.includes('webm') ? 'webm' : 'ogg'}`);
          const res = await fetch('/api/ai/transcribe', {
            method: 'POST',
            credentials: 'include',
            body: fd,
          });
          const data = await res.json() as { text?: string; error?: string };
          if (data.text?.trim()) {
            setInput((prev) => (prev.trim() ? `${prev.trim()} ${data.text!.trim()}` : data.text!.trim()));
            setTimeout(() => composerTextareaRef.current?.focus(), 0);
          }
        } catch {
          // silently ignore transcription errors
        } finally {
          setIsTranscribing(false);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {
      // microphone permission denied or unavailable
    }
  }, []);

  const cancelRecording = React.useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      audioChunksRef.current = []; // discard chunks before stop fires
      recorder.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, []);

  const stopRecording = React.useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, []);

  const isBusy = isSending || isRunningInBackground;
  const canStopDisplay = isSending || isRunningInBackground;
  const canSend = (input.trim().length > 0 || pendingAttachments.length > 0) && !isBusy;
  const treeNodes = React.useMemo(() => treeNodesFromMessages(messages), [messages]);
  const siblingMetas = React.useMemo(() => siblingMetaById(treeNodes), [treeNodes]);
  const resolvedLeaf = resolveActiveLeaf(treeNodes, activeLeafId);
  const visibleMessages = React.useMemo(
    () => visibleMessagesFromTree(messages, resolvedLeaf),
    [messages, resolvedLeaf],
  );
  const hasMessages = messages.length > 0 || isLoadingHistory;
  const latestAssistant = [...visibleMessages].reverse().find((message) => message.role === 'assistant');
  const latestAssistantMessageId = latestAssistant?.id;
  const lastWriteMessageId = lastWriteAssistantMessageId(visibleMessages);
  /** Mirrors the `isSending` prop of the latest assistant row, which renders its own status line. */
  const assistantRowShowsStatus = isSending && Boolean(latestAssistantMessageId);

  const filteredSlashSkills = slashSkills.filter((skill) => {
    const needle = slashQuery.trim().toLocaleLowerCase('de-DE');
    if (!needle) return true;
    return skill.name.toLocaleLowerCase('de-DE').includes(needle)
      || skill.description.toLocaleLowerCase('de-DE').includes(needle);
  }).slice(0, 8);

  const loadSlashSkills = React.useCallback(async () => {
    if (slashLoadedRef.current) return;
    slashLoadedRef.current = true;
    try {
      const res = await fetch('/api/ai/skills', { credentials: 'include' });
      if (!res.ok) {
        slashLoadedRef.current = false;
        return;
      }
      const body = await res.json() as { skills?: SlashSkill[] };
      setSlashSkills((body.skills ?? []).filter((skill) => skill.id && skill.name));
    } catch {
      slashLoadedRef.current = false;
    }
  }, []);

  const syncSlashMenu = (value: string) => {
    const match = /(?:^|\s)\/([^\s]*)$/.exec(value);
    if (!match) {
      setSlashOpen(false);
      return;
    }
    setSlashOpen(true);
    setSlashQuery(match[1] ?? '');
    setSlashIndex(0);
    void loadSlashSkills();
  };

  const selectSlashSkill = (skill: SlashSkill) => {
    setTurnSkills((prev) => (prev.some((item) => item.id === skill.id) ? prev : [...prev, skill].slice(0, 8)));
    setInput((current) => current.replace(/(^|\s)\/[^\s]*$/, '$1').trimEnd());
    setSlashOpen(false);
    composerTextareaRef.current?.focus();
  };

  const composerBar = (
    <div
      className="mx-auto w-full min-w-0 max-w-[700px] rounded-2xl border border-app-border bg-app-surface shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {turnSkills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pt-2">
          {turnSkills.map((skill) => (
            <span key={skill.id} className="inline-flex h-7 max-w-full items-center gap-1 rounded-md border border-app-border bg-app-surface-muted px-2 text-xs text-app-text">
              <span className="truncate">/{skill.name}</span>
              <button
                type="button"
                className="text-app-text-subtle hover:text-app-text"
                aria-label={`${skill.name} entfernen`}
                onClick={() => setTurnSkills((prev) => prev.filter((item) => item.id !== skill.id))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {slashOpen && (
        <div className="relative">
          <div className="absolute bottom-full left-3 z-40 mb-1 max-h-64 w-80 overflow-y-auto rounded-md border border-app-border bg-app-surface py-1 shadow-lg">
            {filteredSlashSkills.length === 0 ? (
              <p className="px-3 py-2 text-sm text-app-text-muted">Keine passenden Skills.</p>
            ) : filteredSlashSkills.map((skill, index) => (
              <button
                key={skill.id}
                type="button"
                className={`flex w-full flex-col px-3 py-1.5 text-left ${index === slashIndex ? 'bg-app-hover' : 'hover:bg-app-hover'}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectSlashSkill(skill);
                }}
              >
                <span className="truncate text-sm text-app-text">{skill.name}</span>
                <span className="truncate text-xs text-app-text-muted">{skill.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {pendingAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1 border-b border-app-border">
          {pendingAttachments.map((s) => (
            <div
              key={s.id}
              className="relative flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface-muted pr-1 pl-1 py-1 text-xs text-app-text-muted max-w-[140px]"
            >
              {s.previewUrl ? (
                <img src={s.previewUrl} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
              ) : s.file.type === 'application/pdf' ? (
                <FileText className="h-8 w-8 shrink-0 text-red-500 p-1" />
              ) : isSpreadsheetFile(s.file) ? (
                <FileSpreadsheet className="h-8 w-8 shrink-0 text-green-600 p-1" />
              ) : (
                <FileText className="h-8 w-8 shrink-0 text-app-text-subtle p-1" />
              )}
              <span className="truncate flex-1 min-w-0">{s.file.name}</span>
              <button
                type="button"
                onClick={() => removePendingAttachment(s.id)}
                className="shrink-0 p-0.5 rounded hover:bg-slate-200 text-app-text-subtle"
                aria-label="Entfernen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 px-3 py-2 min-w-0">
        <div className="relative shrink-0 pb-0.5" ref={plusRef}>
          <button
            type="button"
            onClick={() => setPlusOpen((o) => !o)}
            className="h-8 w-8 rounded-full text-app-text-subtle hover:bg-app-hover flex items-center justify-center"
            aria-expanded={plusOpen}
            aria-haspopup="menu"
          >
            <Plus className="h-4 w-4" />
          </button>
          {plusOpen && (
            <div className="absolute bottom-full left-0 mb-1 z-40 min-w-[200px] rounded-lg border border-app-border bg-app-surface py-1 shadow-lg">
              <button
                type="button"
                className="flex w-full items-center gap-2 whitespace-nowrap px-2.5 py-1.5 text-left text-[13px] text-app-text hover:bg-app-hover"
                onClick={() => {
                  setPlusOpen(false);
                  fileInputRef.current?.click();
                }}
              >
                <Paperclip className="h-3.5 w-3.5 text-app-text-subtle" />
                Dateien oder Fotos hinzufügen
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_INPUT}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) addPendingAttachments(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        <textarea
          ref={composerTextareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            syncSlashMenu(e.target.value);
          }}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (slashOpen && filteredSlashSkills.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              setSlashIndex((current) => {
                const next = e.key === 'ArrowDown' ? current + 1 : current - 1;
                return (next + filteredSlashSkills.length) % filteredSlashSkills.length;
              });
              return;
            }
            if (slashOpen && e.key === 'Escape') {
              e.preventDefault();
              setSlashOpen(false);
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              if (slashOpen && filteredSlashSkills.length > 0) {
                e.preventDefault();
                const skill = filteredSlashSkills[slashIndex] ?? filteredSlashSkills[0];
                if (skill) selectSlashSkill(skill);
                return;
              }
              e.preventDefault();
              void sendMessage();
            }
          }}
          rows={1}
          placeholder="Stell mir eine Frage oder füge Dateien hinzu"
          className="min-w-0 flex-1 bg-transparent border-0 outline-none text-sm text-app-text placeholder:text-slate-400 resize-none leading-5 py-2"
          aria-label="Nachricht"
        />
        {mediaRecorderSupported && (input.length === 0 || isRecording || isTranscribing) ? (
          <button
            type="button"
            onClick={isRecording ? stopRecording : isTranscribing ? undefined : startRecording}
            disabled={isTranscribing}
            className={
              isRecording
                ? 'relative h-8 w-8 shrink-0 rounded-full bg-red-500 text-white flex items-center justify-center mb-0.5'
                : isTranscribing
                  ? 'relative h-8 w-8 shrink-0 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center mb-0.5 cursor-not-allowed'
                  : 'h-8 w-8 shrink-0 rounded-full text-app-text-subtle hover:bg-app-hover flex items-center justify-center mb-0.5'
            }
            aria-label={isRecording ? 'Aufnahme stoppen' : isTranscribing ? 'Transkribiere…' : 'Spracheingabe'}
          >
            {isRecording && (
              <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-60 pointer-events-none" />
            )}
            {isTranscribing
              ? <LoadingSpinner size="sm" className="relative z-10" />
              : isRecording
                ? <MicOff className="h-3.5 w-3.5 relative z-10" />
                : <Mic className="h-3.5 w-3.5" />}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => (canStopDisplay ? void cancelMessage() : void sendMessage())}
          disabled={!canStopDisplay && !canSend}
          className="group h-8 w-8 shrink-0 rounded-full bg-pipedrive-blue text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center mb-0.5"
          aria-label={canStopDisplay ? 'Antwort abbrechen' : 'Senden'}
          title={
            isRunningInBackground
              ? 'Bricht die laufende Antwort ab. Der bisherige Stand bleibt im Chat.'
              : undefined
          }
        >
          {canStopDisplay ? (
            <>
              <LoadingSpinner size="sm" className="group-hover:hidden" />
              <Square className="h-3.5 w-3.5 hidden group-hover:block fill-current" />
            </>
          ) : (
            <ArrowUp className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="h-full min-h-0 relative overflow-hidden text-app-text"
      onDragOver={hasMessages ? onDragOver : undefined}
      onDrop={hasMessages ? onDrop : undefined}
    >

      {/* Nur die Meldung selbst fängt Klicks ab und ist so schmal wie ihr Text; mit dem X lässt
          sie sich schließen, damit nichts darunter verdeckt bleibt. */}
      {chatError && (
        <div className={`pointer-events-none absolute top-3 left-0 right-0 z-30 px-4 ${VIEWPORT_CENTER}`}>
          <div className="pointer-events-auto mx-auto flex w-fit max-w-[min(100%,860px)] items-start gap-2 rounded-lg border border-red-200 bg-red-50 py-2 pl-3 pr-1.5 text-xs text-red-700">
            <span className="min-w-0">{chatError}</span>
            <button
              type="button"
              onClick={() => setChatError(null)}
              className="-my-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-red-500 hover:bg-red-100 hover:text-red-700"
              aria-label="Schließen"
              title="Schließen"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {historyError && messages.length === 0 ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center">
            <p className="text-sm text-red-700">{historyError}</p>
            <button
              type="button"
              onClick={() => void loadHistory()}
              className="app-btn-danger-outline mt-3 inline-flex h-9 items-center rounded-md border border-red-200 bg-app-surface px-3.5 text-sm font-medium text-red-700 hover:bg-red-100"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      ) : hasMessages ? (
        <div
          ref={viewportRef}
          onScroll={onViewportScroll}
          className={`app-scrollbar absolute inset-0 overflow-y-auto overscroll-contain px-6 pt-10 pb-32 ${VIEWPORT_TOP_FADE}`}
        >
          <div className={`w-full ${VIEWPORT_CENTER}`}>
            <div className="max-w-[860px] mx-auto space-y-5">
              {isLoadingHistory && messages.length === 0 && (
                <div className="space-y-5 animate-pulse">
                  {[72, 48, 88, 56].map((w, i) => (
                    <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                      <div className={`h-8 rounded-2xl bg-slate-200 ${i % 2 === 0 ? 'rounded-tl-sm' : 'rounded-tr-sm'}`} style={{ width: `${w}%` }} />
                    </div>
                  ))}
                </div>
              )}
              {visibleMessages.map((msg) => {
                const forks = siblingMetas.get(msg.id);
                const isLastWrite = Boolean(lastWriteMessageId) && msg.id === lastWriteMessageId;
                const isEditing = editingUserId === msg.id;
                return (
                  <ChatMessageItem
                    key={msg.id}
                    msg={msg}
                    isStreaming={isSending && msg.id === latestAssistantMessageId}
                    isLatestAssistant={msg.id === latestAssistantMessageId}
                    isBusy={isBusy}
                    showUndo={isLastWrite && !isBusy}
                    undoBusy={isLastWrite && undoBusy}
                    undoHint={isLastWrite ? undoHint : null}
                    feedback={msg.requestId ? feedbackByRequest[msg.requestId] ?? null : null}
                    siblingIndex={forks?.index}
                    siblingTotal={forks?.total}
                    prevSiblingId={forks && forks.index > 1 ? forks.siblingIds[forks.index - 2] : undefined}
                    nextSiblingId={forks && forks.index < forks.total ? forks.siblingIds[forks.index] : undefined}
                    isEditing={isEditing}
                    editDraft={isEditing ? editDraft : ''}
                    onContinue={onContinueItem}
                    onUndo={onUndoItem}
                    onSwitchVersion={onSwitchVersionItem}
                    onRate={onRateItem}
                    onStartEdit={onStartEditItem}
                    onCancelEdit={onCancelEditItem}
                    onEditDraftChange={setEditDraft}
                    onSubmitEdit={onSubmitEditItem}
                    onPreview={onPreviewItem}
                  />
                );
              })}
              {isBusy && (isRunningInBackground || !assistantRowShowsStatus) && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 text-app-text-subtle text-sm">
                    <LoadingSpinner />
                    {isRunningInBackground
                      ? 'Der Assistent arbeitet im Hintergrund weiter…'
                      : streamStepCount > 0
                        ? `Bearbeitungsschritt ${streamStepCount}${streamMaxSteps ? ` von ${streamMaxSteps}` : ''}…`
                        : ACTIVITY_THINKING}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6" onDragOver={onDragOver} onDrop={onDrop}>
          <div className={`w-full ${VIEWPORT_CENTER}`}>
            <div className="mx-auto flex w-full max-w-[860px] flex-col items-center gap-5">
              <p className="text-[34px] font-medium tracking-tight text-app-text text-center">
                Wie kann ich dir den Rücken freihalten?
              </p>
              {composerBar}
              <p className="text-xs text-app-text-subtle text-center">Dateien hierher ziehen oder über + einfügen (JPEG, PNG, PDF, Excel, CSV, Text)</p>
            </div>
          </div>
        </div>
      )}

      {hasMessages ? (
        <div className={`absolute bottom-6 left-0 right-0 z-20 px-4 ${VIEWPORT_CENTER}`}>
          {backgroundDismissedHint ? (
            <p className="mx-auto mb-2 max-w-[700px] text-center text-xs text-app-text-subtle">
              {backgroundDismissedHint}
            </p>
          ) : null}
          {composerBar}
        </div>
      ) : null}

      <PdfPreviewModal
        open={docPreview !== null}
        fileUrl={docPreview?.url ?? null}
        fileName={docPreview?.filename ?? 'Dokument'}
        onClose={() => setDocPreview(null)}
      />
    </div>
  );
}
